import { ApiService } from "./api.service";
import type { TTeamsSignInStart } from "@/types/msteams";
import type { TTeamsDigestSendResult, TTeamsSelfLinks } from "@/types/msteams-link";

/** The caller's **own** Microsoft Teams links (plan §5a FR5–FR7, NG-1134).
 *
 *  Self-scoped, so no path takes a workspace slug or a link id — `users/me/…` and the
 *  session decides the rows. That is the structural difference from
 *  `MsTeamsConnectionService` beside this one, which is admin-only and addresses one
 *  workspace's consent: a person's links span every workspace they are a member of,
 *  and they get read and written as a set. `sendDigestNow` (NG-1641) is the one call
 *  that has to name a workspace, and it names it in the **body**: the caller is still
 *  the session, but a summary is about one workspace's work and a person may be
 *  linked in several.
 *
 *  Response-time budgets (`/response-time-limits`): `getLinks` and `unlink` are
 *  bounded single-digit-row reads/writes and target the sub-1s read / sub-2.5s write
 *  band. `startSignIn` mints an Entra authorization URL through `msal` and sits in the
 *  1–10s band with a visible pending state owned by the block that calls it.
 *  `sendDigestNow` is a write in the sub-2.5s band; see its own note.
 */
export class MsTeamsLinkService extends ApiService {
  private readonly base = "/api/users/me/integrations/msteams/links";

  /** Every workspace of the caller's that has a Teams connection, with their link on
   *  it. Never a 404 and never an empty-vs-absent distinction: a person with no
   *  connected workspace gets `{ workspaces: [] }`, which the UI renders as nothing at
   *  all rather than as an error. */
  async getLinks(): Promise<TTeamsSelfLinks> {
    return this.get(`${this.base}/`);
  }

  /** Where to send the person's browser to prove their Microsoft identity.
   *
   *  A URL rather than a 302, for the same reason the workspace sign-in is: a redirect
   *  to Microsoft on an XHR is followed by the browser and lands a cross-origin HTML
   *  page in a JSON parser.
   *
   *  `returnPath` is the web-app path the callback comes back to with
   *  `msteams_connected=1` or `msteams_error=<code>` appended; the server refuses
   *  anything that is not a single-slash path. Answers 503
   *  (`code: "signin_not_configured"`) where this environment has no sign-in
   *  registration — this flow's whole per-environment gate, exactly as PR 5's.
   */
  async startSignIn(returnPath: string): Promise<TTeamsSignInStart> {
    return this.post(`${this.base}/sign-in/`, { return_path: returnPath });
  }

  /** Opt out of Teams notifications everywhere at once.
   *
   *  **Global for the person, not per workspace** (FR7): every live link of theirs
   *  becomes `unlinked` and its pending deliveries are dropped. That is the promise
   *  the control makes — somebody switching this off does not expect a second
   *  workspace to keep messaging them — and per-workspace unlink is explicitly out of
   *  scope for NG-1134. Idempotent, and answers with the same payload as the read so
   *  the caller replaces its view rather than patching it.
   */
  async unlink(): Promise<TTeamsSelfLinks> {
    return this.post(`${this.base}/unlink/`);
  }

  /** Send me my Monday summary now (NG-1641) — a demo affordance for Product, and
   *  the one manual trigger for a card the scheduler otherwise builds on a Monday.
   *
   *  **Not under `${this.base}`**, which is the links collection: this acts on the
   *  digest, so it sits beside it at `…/msteams/digest/send/`. The path is spelled
   *  out for that reason rather than assembled from the links base, which would read
   *  as a third verb on the links resource.
   *
   *  **A workspace slug in the body, not the path.** The endpoint is self-scoped like
   *  its neighbours — the session says who — but a person can be linked in several
   *  workspaces and each gets its own card about its own work, so which one is a fact
   *  the caller has to state and the server will not guess (`no_workspace`).
   *
   *  **Two success shapes, both resolved.** A 202 says the card went (with the totals
   *  it counted); a 200 says nothing was due, which is not a failure and must not
   *  reach the caller as one. Everything else — 400/404 refusals and the two 409s —
   *  rejects like every other call here, carrying a `code` in the body that
   *  `digestRefusalCode` reads.
   *
   *  Response-time budget (`/response-time-limits`): a write that builds and posts a
   *  card through the Bot Connector, so the sub-2.5s p99 write band with the calling
   *  control disabled for the round trip.
   */
  async sendDigestNow(workspaceSlug: string): Promise<TTeamsDigestSendResult> {
    return this.post("/api/users/me/integrations/msteams/digest/send/", {
      workspace_slug: workspaceSlug,
    });
  }
}
